import { useCallback, useEffect, useRef, useState } from 'react';
import client from '../../../shared/api/client';
import { toastError } from '../../../shared/toastHelper';

export const CUES_PATH = '/conductor/venue_chat/scheduled_tasks';
export const INSIGHTS_PATH = '/conductor/venue_chat/insights';
export const CUE_RUNS_PATH = '/conductor/venue_chat/scheduled_task_runs';
const CUE_PARAMS = {};
const INSIGHT_PARAMS = { mark_seen: true, exclude_cue_results: true };
const CUE_RUN_PARAMS = { mark_seen: true };
const CUE_RUNS_ERROR = "Couldn't load your past cues. Try again.";
const CUES_ERROR = "Couldn't load your cues. Try again.";
const INSIGHTS_ERROR = "Couldn't load cues from Opendate. Try again.";

const useFetchedList = (path, key, params, errorMessage) => {
  const [items, setItems] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(false);
    client.get(path, { params })
      .then((response) => {
        if (ignore) return;
        setItems(response.data[key]);
        setData(response.data);
      })
      .catch(() => {
        if (ignore) return;
        setError(true);
        toastError(errorMessage);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [attempt, path, key, params, errorMessage]);

  const reload = useCallback(() => setAttempt((count) => count + 1), []);

  return { items, setItems, data, loading, error, reload };
};

export function useConductorCues() {
  const { items: cues, setItems: setCues, data, loading, error, reload } = useFetchedList(CUES_PATH, 'scheduled_tasks', CUE_PARAMS, CUES_ERROR);
  const deliveryOptions = data?.delivery_options || [];
  const emailOptions = data?.email_options || [];
  const deliveryChangesInFlight = useRef(new Set());
  const [updatingCueIds, setUpdatingCueIds] = useState([]);

  const changeDelivery = useCallback(async (cue, deliveryTarget) => {
    if (deliveryChangesInFlight.current.has(cue.id)) return;
    deliveryChangesInFlight.current.add(cue.id);
    setUpdatingCueIds((current) => [...current, cue.id]);
    try {
      const response = await client.patch(`${CUES_PATH}/${cue.id}`, { delivery_target: deliveryTarget });
      setCues((current) => (current || []).map((item) => (item.id === cue.id ? response.data : item)));
    } catch (e) {
      toastError("Couldn't move that cue. Try again.");
    } finally {
      deliveryChangesInFlight.current.delete(cue.id);
      setUpdatingCueIds((current) => current.filter((id) => id !== cue.id));
    }
  }, [setCues]);

  const changeEmailRecipients = useCallback(async (cue, ids) => {
    if (deliveryChangesInFlight.current.has(cue.id)) return;
    deliveryChangesInFlight.current.add(cue.id);
    setUpdatingCueIds((current) => [...current, cue.id]);
    try {
      const response = await client.patch(`${CUES_PATH}/${cue.id}`, { email_team_membership_ids: ids });
      setCues((current) => (current || []).map((item) => (item.id === cue.id ? response.data : item)));
    } catch (e) {
      toastError("Couldn't update who gets this cue's email. Try again.");
    } finally {
      deliveryChangesInFlight.current.delete(cue.id);
      setUpdatingCueIds((current) => current.filter((id) => id !== cue.id));
    }
  }, [setCues]);

  const cancelCue = useCallback(async (cue) => {
    setCues((current) => (current || []).filter((item) => item.id !== cue.id));
    try {
      await client.patch(`${CUES_PATH}/${cue.id}/cancel`);
    } catch (e) {
      toastError("Couldn't cancel that cue. Try again.");
      reload();
    }
  }, [setCues, reload]);

  return { cues, deliveryOptions, emailOptions, updatingCueIds, loading, error, reload, cancelCue, changeDelivery, changeEmailRecipients };
}

export function usePastCues() {
  const { items: runs, loading, error, reload } = useFetchedList(CUE_RUNS_PATH, 'scheduled_task_runs', CUE_RUN_PARAMS, CUE_RUNS_ERROR);

  useEffect(() => {
    if (!runs) return;
    window.dispatchEvent(new CustomEvent('conductorInsightsRefreshRequested'));
  }, [runs]);

  return { runs, loading, error, reload };
}

export function useOpendateCues() {
  const { items: insights, setItems: setInsights, data, loading, error, reload } = useFetchedList(INSIGHTS_PATH, 'insights', INSIGHT_PARAMS, INSIGHTS_ERROR);

  useEffect(() => {
    if (!data) return;
    window.dispatchEvent(new CustomEvent('conductorInsightsChanged', { detail: { pendingCount: data.pending_count } }));
  }, [data]);

  const dismissInsight = useCallback(async (insight) => {
    setInsights((current) => (current || []).filter((item) => item.id !== insight.id));
    try {
      await client.patch(`${INSIGHTS_PATH}/${insight.id}/dismiss`);
      window.dispatchEvent(new CustomEvent('conductorInsightsRefreshRequested'));
    } catch (e) {
      toastError("Couldn't dismiss that cue. Try again.");
      reload();
    }
  }, [setInsights, reload]);

  return { insights, loading, error, reload, dismissInsight };
}
